import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { INotification } from '../../../assets/types/dtoTypes';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { AuthenticationService } from './authentication.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable( { providedIn: 'root' } )
export class NotificationService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private authService = inject(AuthenticationService);

  private notifsUrl = `${environment.apiUrl}/notifications`;

  notifications = new BehaviorSubject<INotification[]>( [] );

  private unreadCount = new BehaviorSubject<number>( 0 );

  private stompClient: any;

  private offerCreatedSubject = new Subject<any>();

  private applicationUpdatedSubject = new Subject<any>();

  constructor() {
    this.initializeWebSocketConnection();
  }

  fetchNotifications( id: string ): void {
    this.getNotifications( id ).subscribe( ( notifications ) => {
      this.notifications.next( notifications );
      this.updateUnreadCount();
    } );
  }

  getUnreadCount(): Observable<number> {
    return this.unreadCount.asObservable();
  }

  markAsRead( notificationId: string ): void {
    const notifications = this.notifications.getValue().map( ( notification ) => {
      if ( notification.id === notificationId ) {
        notification.isRead = true;
      }
      return notification;
    } );
    this.notifications.next( notifications );
    this.updateUnreadCount();

    const url = `${this.notifsUrl}/mark-as-read?notificationId=${notificationId}`;
    this.http.post( url, {} ).subscribe();
  }

  getNotifications( id: string ): Observable<INotification[]> {
    const url = `${this.notifsUrl}/unread?userId=${id}`;
    return this.http.get<INotification[]>( url );
  }

  private initializeWebSocketConnection(): void {
    const serverUrl = `${environment.socketsUrl}`;
    this.stompClient = Stomp.over( () => new SockJS( serverUrl ) );

    this.stompClient.connect( {}, () => {

      this.stompClient.subscribe( '/topic/jobOffer', ( message ) => {
        const offer = JSON.parse( message.body );
        this.offerCreatedSubject.next( offer );

      } );

      this.stompClient.subscribe( '/topic/application', ( message ) => {
        const application = JSON.parse( message.body );
        this.applicationUpdatedSubject.next( application );
        const currentUserId = this.authService.getIdFromAccessToken();
        const companyId = this.authService.getCompanyIdFromAccessToken();


        if ( !companyId ) {
          if ( application.userId === currentUserId ) {
            this.translate.get( 'notifications.application_status_change', {
              oldStatus: application.oldStatus,
              newStatus: application.newStatus,
            } ).subscribe( ( translatedMessage: string ) => {
              const notif: INotification = {
                id: crypto.randomUUID(),
                message: translatedMessage,
                timestamp: new Date().toISOString(),
                type: 'APPLICATION_UPDATED',
                isRead: false,
                userId: currentUserId,
              };

              this.addNotification( notif );
              this.showWebNotification( notif );
            } );
          }
        } else {
          if ( application.companyId === companyId ) {
            this.translate.get( 'notifications.company_status_change', {
              oldStatus: application.oldStatus,
              newStatus: application.newStatus,
            } ).subscribe( ( translatedMessage: string ) => {
              const notif: INotification = {
                id: crypto.randomUUID(),
                message: translatedMessage,
                timestamp: new Date().toISOString(),
                type: 'APPLICATION_UPDATED',
                isRead: false,
                userId: currentUserId,
              };

              this.addNotification( notif );
              this.showWebNotification( notif );
            } );
          }
        }



      } );

    }, this.handleConnectionError.bind( this ) );


  }

  onApplicationUpdated(): Observable<any> {
    return this.applicationUpdatedSubject.asObservable();
  }

  onOfferCreated(): Observable<any> {
    return this.offerCreatedSubject.asObservable();
  }

  private handleConnectionError( error: any ): void {
    console.error( 'WebSocket connection error:', error );
  }

  triggerChatMessageNotification( senderName: string ): void {
    const currentUserId = this.authService.getIdFromAccessToken();
    this.translate.get( 'notifications.new_message', { sender: senderName } ).subscribe( ( message: string ) => {
      const notif: INotification = {
        id: crypto.randomUUID(),
        message,
        timestamp: new Date().toISOString(),
        type: 'NEW_MESSAGE',
        isRead: false,
        userId: currentUserId,
      };
      this.addNotification( notif );
      this.showWebNotification( notif );
    } );
  }

  private addNotification( notification: INotification ): void {
    const currentNotifications = this.notifications.getValue();
    this.notifications.next( [ notification, ...currentNotifications ] );
    this.updateUnreadCount();
  }

  private updateUnreadCount(): void {
    const unreadNotificationsCount = this.notifications.getValue().filter( ( notification ) => !notification.isRead ).length;
    this.unreadCount.next( unreadNotificationsCount );
  }

  private showWebNotification( notification: INotification ): void {
    if ( Notification.permission === 'granted' ) {
      const webNotification = new Notification( notification.message, {
        body: notification.message,
        icon: 'assets/light-logo.svg',
      } );
      webNotification.onclick = () => window.focus();
    }
  }
}
